/**
 * Independent CSV runtime store — per-floor in-memory state.
 * No longer proxies through floorStore; owns its own Zustand store.
 */

import { create } from 'zustand'
import { shallow } from 'zustand/shallow'
import type { CsvRuntimeState, CsvUploadState } from '../../../map-editor-shared/store/csvRuntimeTypes'
import { createEmptyCsvRuntime } from '../../../map-editor-shared/store/csvRuntimeTypes'
import type { ParseResult } from '../lib/csvParser'
import type { GroupedLayer } from '../lib/layerGrouper'
import { csvWorkerClient } from '../lib/csvWorkerClient'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'
import { perfMonitor } from '../../../map-editor-shared/lib/performanceMonitor'
import type { SidecarItem } from '../../cctv-ip-import'

export type UploadState = CsvUploadState
export type CSVState = CsvRuntimeState

interface CsvStoreState {
  excludeIsolatedOnImport: boolean
  /** Per-floor CSV runtime (in-memory only, not persisted). */
  csvRuntimeByFloor: Record<string, CsvRuntimeState>
  /** Current floor id — synced externally via setCurrentFloor. */
  currentFloor: string | null
  /**
   * One-shot stash of CCTV IPs (meter coords) from the latest DXF conversion.
   * Captured by useLayerSelection, drained by EditorPage once CCTV elements
   * render (auto IP wiring). Empty when nothing pending.
   */
  cctvIps: SidecarItem[]
  /**
   * DXF 업로드 파이프라인 신호 — EditorGuide(가이드 맵 생성)의 풀페이지 카드가
   * 언제 비켜줘야 하는지 판단하는 데 쓴다. ServerCADList/useCSVUploadState 처럼
   * 깊이 중첩된 컴포넌트에서 프롭 없이 바로 반영하기 위해 전역에 둔다.
   * dxfListReady: 서버 DXF 목록 조회가 끝나 목록(파일 있음/업로드 버튼)이
   * 화면에 뜬 상태 — 파일 선택은 항상 사용자가 직접 한다(자동 선택 없음).
   */
  dxfListReady: boolean
  dxfLayerModalOpen: boolean
}

type CsvStoreActions = {
  setExcludeIsolatedOnImport: (enabled: boolean) => void
  // --- Public facade (backward-compat) ---
  setFile: (file: File) => void
  setUploadState: (state: UploadState) => void
  clearFile: () => void
  parseFile: () => Promise<void>
  toggleLayer: (layerName: string) => void
  selectAllLayers: () => void
  deselectAllLayers: () => void

  // --- CCTV IP stash (cross-floor one-shot) ---
  setCctvIps: (items: SidecarItem[]) => void

  // --- DXF 업로드 파이프라인 신호 (가이드 카드용) ---
  setDxfListReady: (ready: boolean) => void
  setDxfLayerModalOpen: (open: boolean) => void

  // --- Per-floor runtime management ---
  setCurrentFloor: (floorId: string | null) => void
  getCsvRuntimeForFloor: (floorId: string | null) => CsvRuntimeState
  mergeCsvRuntimeForFloor: (floorId: string, partial: Partial<CsvRuntimeState>) => void
  clearCsvRuntimeForFloor: (floorId: string) => void
  clearAllCsvRuntime: () => void
  csvToggleLayerForFloor: (floorId: string, layerName: string) => void
  csvSelectAllLayersForFloor: (floorId: string) => void
  csvDeselectAllLayersForFloor: (floorId: string) => void
  parseCsvFileForFloor: (floorId: string) => Promise<void>
}

type CsvStore = CsvStoreState & CsvStoreActions

const useCsvStoreInternal = create<CsvStore>((set, get) => ({
  excludeIsolatedOnImport: true,
  setExcludeIsolatedOnImport: (enabled) => set({ excludeIsolatedOnImport: enabled }),
  csvRuntimeByFloor: {},
  currentFloor: null,
  cctvIps: [],
  dxfListReady: false,
  dxfLayerModalOpen: false,

  // --- CCTV IP stash ---
  setCctvIps: (items) => set({ cctvIps: items }),

  // --- DXF 업로드 파이프라인 신호 ---
  setDxfListReady: (ready) => set({ dxfListReady: ready }),
  setDxfLayerModalOpen: (open) => set({ dxfLayerModalOpen: open }),

  // --- Sync current floor from external (floorStore) ---
  setCurrentFloor: (floorId) => set({ currentFloor: floorId }),

  // --- Per-floor runtime ---
  getCsvRuntimeForFloor: (floorId) => {
    if (!floorId) return createEmptyCsvRuntime()
    return get().csvRuntimeByFloor[floorId] ?? createEmptyCsvRuntime()
  },

  mergeCsvRuntimeForFloor: (floorId, partial) => {
    const state = get()
    const prev = state.csvRuntimeByFloor[floorId] ?? createEmptyCsvRuntime()
    const next: CsvRuntimeState = {
      ...prev,
      ...partial,
      selectedLayers: partial.selectedLayers ?? prev.selectedLayers,
      uploadState: partial.uploadState ?? prev.uploadState,
      parsedData: partial.parsedData !== undefined ? partial.parsedData : prev.parsedData,
      groupedLayers: partial.groupedLayers !== undefined ? partial.groupedLayers : prev.groupedLayers,
      rawData: partial.rawData !== undefined ? partial.rawData : prev.rawData,
      file: partial.file !== undefined ? partial.file : prev.file,
    }
    set({
      csvRuntimeByFloor: {
        ...state.csvRuntimeByFloor,
        [floorId]: next,
      },
    })
  },

  clearCsvRuntimeForFloor: (floorId) => {
    const state = get()
    const { [floorId]: _removed, ...rest } = state.csvRuntimeByFloor
    set({ csvRuntimeByFloor: rest })
  },

  clearAllCsvRuntime: () => set({ csvRuntimeByFloor: {} }),

  csvToggleLayerForFloor: (floorId, layerName) => {
    const prev = get().getCsvRuntimeForFloor(floorId)
    const newSelected = new Set(prev.selectedLayers)
    if (newSelected.has(layerName)) {
      newSelected.delete(layerName)
    } else {
      newSelected.add(layerName)
    }
    get().mergeCsvRuntimeForFloor(floorId, { selectedLayers: newSelected })
  },

  csvSelectAllLayersForFloor: (floorId) => {
    const prev = get().getCsvRuntimeForFloor(floorId)
    const { groupedLayers } = prev
    if (!groupedLayers) return
    const allLayers = new Set(groupedLayers.map((layer) => layer.layer))
    get().mergeCsvRuntimeForFloor(floorId, { selectedLayers: allLayers })
  },

  csvDeselectAllLayersForFloor: (floorId) => {
    get().mergeCsvRuntimeForFloor(floorId, { selectedLayers: new Set<string>() })
  },

  parseCsvFileForFloor: async (floorId) => {
    const rt = get().getCsvRuntimeForFloor(floorId)
    const { file } = rt
    if (!file) return

    try {
      perfMonitor.startSession()
      get().mergeCsvRuntimeForFloor(floorId, { uploadState: { status: 'parsing' } })

      log.info(LogCategory.CSV, 'Reading CSV file', { fileName: file.name, size: file.size })

      const readStart = performance.now()
      const content = await file.text()
      const readEnd = performance.now()
      perfMonitor.record('csvFileRead', readEnd - readStart)

      log.perf.start('CSV Parsing (Web Worker)')
      log.info(LogCategory.CSV, 'Parsing CSV in Web Worker (non-blocking)', {
        contentLength: content.length,
        fileReadTime: `${(readEnd - readStart).toFixed(2)}ms`,
      })

      const workerStart = performance.now()
      const { parsedData, groupedLayers } = await csvWorkerClient.parseCSV(content, get().excludeIsolatedOnImport)
      const workerEnd = performance.now()
      perfMonitor.record('csvWorkerParsing', workerEnd - workerStart)

      log.perf.end('CSV Parsing (Web Worker)')
      log.info(LogCategory.CSV, 'Web Worker parsing completed', {
        workerTime: `${(workerEnd - workerStart).toFixed(2)}ms`,
        entitiesCount: parsedData.entities.length,
        layersCount: groupedLayers.length,
      })

      if (parsedData.entities.length === 0 && parsedData.errors.length > 0) {
        log.error(LogCategory.CSV, 'CSV parsing failed with errors', {
          errorCount: parsedData.errors.length,
          errors: parsedData.errors,
        })
        get().mergeCsvRuntimeForFloor(floorId, {
          uploadState: {
            status: 'error',
            message: parsedData.errors.join(', '),
          },
        })
        return
      }

      if (parsedData.errors.length > 0) {
        log.warn(LogCategory.CSV, 'CSV parsing completed with errors', {
          errorCount: parsedData.errors.length,
          entityCount: parsedData.entities.length,
          errors: parsedData.errors.slice(0, 5),
        })
      }

      const allLayerNames = new Set(groupedLayers.map((layer) => layer.layer))

      log.info(LogCategory.CSV, 'CSV parsing successful', {
        entityCount: parsedData.entities.length,
        layerCount: groupedLayers.length,
        rowCount: parsedData.rowCount,
      })

      get().mergeCsvRuntimeForFloor(floorId, {
        rawData: content,
        parsedData,
        groupedLayers,
        selectedLayers: allLayerNames,
        uploadState: {
          status: 'parsed',
          fileName: file.name,
          rowCount: parsedData.rowCount,
        },
      })
    } catch (error) {
      log.error(LogCategory.CSV, 'CSV parsing failed', error)
      get().mergeCsvRuntimeForFloor(floorId, {
        uploadState: {
          status: 'error',
          message: error instanceof Error ? error.message : 'Failed to parse CSV file',
        },
      })
    }
  },

  // --- Facade actions (use currentFloor) ---
  setFile: (file) => {
    const id = get().currentFloor
    if (!id) return
    get().mergeCsvRuntimeForFloor(id, { file })
  },
  setUploadState: (uploadState) => {
    const id = get().currentFloor
    if (!id) return
    get().mergeCsvRuntimeForFloor(id, { uploadState })
  },
  clearFile: () => {
    const id = get().currentFloor
    if (!id) {
      get().clearAllCsvRuntime()
      return
    }
    get().clearCsvRuntimeForFloor(id)
  },
  parseFile: async () => {
    const id = get().currentFloor
    if (!id) return
    await get().parseCsvFileForFloor(id)
  },
  toggleLayer: (layerName) => {
    const id = get().currentFloor
    if (!id) return
    get().csvToggleLayerForFloor(id, layerName)
  },
  selectAllLayers: () => {
    const id = get().currentFloor
    if (!id) return
    get().csvSelectAllLayersForFloor(id)
  },
  deselectAllLayers: () => {
    const id = get().currentFloor
    if (!id) return
    get().csvDeselectAllLayersForFloor(id)
  },
}))

// --- Backward-compatible facade ---

type CsvStoreSnapshot = CsvRuntimeState & {
  setFile: (file: File) => void
  setUploadState: (state: UploadState) => void
  clearFile: () => void
  parseFile: () => Promise<void>
  toggleLayer: (layerName: string) => void
  selectAllLayers: () => void
  deselectAllLayers: () => void
}

function buildSnapshot(s: CsvStore): CsvStoreSnapshot {
  const id = s.currentFloor
  const base = id ? s.csvRuntimeByFloor[id] ?? createEmptyCsvRuntime() : createEmptyCsvRuntime()
  return {
    ...base,
    setFile: s.setFile,
    setUploadState: s.setUploadState,
    clearFile: s.clearFile,
    parseFile: s.parseFile,
    toggleLayer: s.toggleLayer,
    selectAllLayers: s.selectAllLayers,
    deselectAllLayers: s.deselectAllLayers,
  }
}

function useCSVStoreImpl<T>(selector: (state: CsvStoreSnapshot) => T): T
function useCSVStoreImpl(): CsvStoreSnapshot
function useCSVStoreImpl<T>(selector?: (state: CsvStoreSnapshot) => T): T | CsvStoreSnapshot {
  const hasSelector = typeof selector === 'function'
  return useCsvStoreInternal(
    (s) => {
      const merged = buildSnapshot(s)
      return hasSelector ? (selector as (state: CsvStoreSnapshot) => T)(merged) : merged
    },
    hasSelector ? undefined : shallow
  ) as T | CsvStoreSnapshot
}

export const useCSVStore = Object.assign(useCSVStoreImpl, {
  getState: (): CsvStoreSnapshot => buildSnapshot(useCsvStoreInternal.getState()),
  setState: (partial: Partial<CsvRuntimeState>): void => {
    const id = useCsvStoreInternal.getState().currentFloor
    if (!id) return
    useCsvStoreInternal.getState().mergeCsvRuntimeForFloor(id, partial)
  },
})

/** Direct access to the internal store for per-floor operations. */
export const useCsvRuntimeStore = useCsvStoreInternal

export type { ParseResult, GroupedLayer }
